import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle, CalendarClock } from "lucide-react";
import type { WorkOrderReservation } from "@/lib/workOrderReservations";
import { visitRangeConflictsWithReservations } from "@/lib/workOrderReservations";

export type VisitDuration = "1h" | "2h" | "half-day" | "full-day";

interface VisitSchedulingPanelProps {
  severity: string;
  propertyTimezone?: string | null;
  reservations: WorkOrderReservation[];
  onConfirm: (startIso: string, endIso: string) => void;
  onRequestConflict: (startIso: string, endIso: string) => void;
  confirmPending?: boolean;
  requestPending?: boolean;
  disabled?: boolean;
}

function buildVisitRange(
  dateStr: string,
  timeStr: string,
  duration: VisitDuration
): { start: Date; end: Date } | null {
  if (!dateStr || !timeStr) return null;
  const start = new Date(`${dateStr}T${timeStr}:00`);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start);
  switch (duration) {
    case "1h":
      end.setHours(end.getHours() + 1);
      break;
    case "2h":
      end.setHours(end.getHours() + 2);
      break;
    case "half-day":
      end.setHours(end.getHours() + 4);
      break;
    case "full-day":
      end.setHours(end.getHours() + 8);
      break;
    default:
      return null;
  }
  return { start, end };
}

export function VisitSchedulingPanel({
  severity,
  reservations,
  onConfirm,
  onRequestConflict,
  confirmPending,
  requestPending,
  disabled,
}: VisitSchedulingPanelProps) {
  const [open, setOpen] = useState(false);
  const [visitDate, setVisitDate] = useState("");
  const [visitTime, setVisitTime] = useState("10:00");
  const [duration, setDuration] = useState<VisitDuration>("1h");

  const range = useMemo(
    () => buildVisitRange(visitDate, visitTime, duration),
    [visitDate, visitTime, duration]
  );

  const hasConflict = useMemo(() => {
    if (!range) return false;
    return visitRangeConflictsWithReservations(range.start, range.end, reservations);
  }, [range, reservations]);

  const isHighSeverity = severity === "high" || severity === "urgent";

  const minDate = useMemo(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  }, []);

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <CalendarClock className="h-3.5 w-3.5 mr-1.5" />
        Schedule a visit
      </Button>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border p-3 bg-background">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Date</Label>
          <Input
            type="date"
            min={minDate}
            value={visitDate}
            onChange={(e) => setVisitDate(e.target.value)}
            className="mt-1 h-9"
          />
        </div>
        <div>
          <Label className="text-xs">Start time</Label>
          <Input
            type="time"
            value={visitTime}
            onChange={(e) => setVisitTime(e.target.value)}
            className="mt-1 h-9"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs">Duration</Label>
        <Select value={duration} onValueChange={(v) => setDuration(v as VisitDuration)}>
          <SelectTrigger className="mt-1 h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1h">1 hour</SelectItem>
            <SelectItem value="2h">2 hours</SelectItem>
            <SelectItem value="half-day">Half day (~4h)</SelectItem>
            <SelectItem value="full-day">Full day (~8h)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {range && !hasConflict && (
        <p className="text-sm text-green-700 flex items-center gap-1">
          <CheckCircle className="h-4 w-4 shrink-0" />
          Unit is available
        </p>
      )}

      {range && hasConflict && !isHighSeverity && (
        <p className="text-sm text-muted-foreground">
          This date is during a guest stay. Please pick an available date.
        </p>
      )}

      {range && hasConflict && isHighSeverity && (
        <div className="rounded-md border border-amber-400 bg-amber-50 text-amber-800 p-2.5 text-sm space-y-2">
          <p>
            This date has a guest. For urgent issues the host can request access — tap below to
            send this request.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-amber-400 bg-white hover:bg-amber-50"
            disabled={requestPending || disabled}
            onClick={() => onRequestConflict(range.start.toISOString(), range.end.toISOString())}
          >
            Request this time
          </Button>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!range || hasConflict || confirmPending || disabled}
          onClick={() => range && onConfirm(range.start.toISOString(), range.end.toISOString())}
        >
          <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
          Confirm visit
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
